import {Button, message, Space, Table, Tooltip} from 'antd';
import Style from './index.module.scss';
import type {ColumnsType} from 'antd/es/table';
import {useMutation, useQuery} from 'react-query';
import {LinkButton} from 'Features/Buttons/LinkButton';
import {AgentDataSet} from '../../../agentDataSetService';
import useSelection from 'Utils/Hooks/useSelection';
import Title from 'Components/Title';
import React from 'react';
import DeleteConfirm from 'Components/DeleteConfirm';
import useModalHandler from 'Utils/Hooks/useModalHandler';
import DataSetSelectDrawer from '../DataSetSelectDrawer';
import DataSetEditModal from '../DataSetEditModal';
import StatusTag from '../../../../../../../../Components/StatusTag';
import {QuestionCircleOutlined} from '@ant-design/icons';

interface Props {
    agentId: string;
}

const Index = ({agentId}: Props) => {
    const dataSetSelectDrawer = useModalHandler({
        component: DataSetSelectDrawer,
        type: 'drawer',
    });
    const dataSetEditModal = useModalHandler({
        component: DataSetEditModal,
    });
    const rowSelection = useSelection();

    const getData = useQuery({
        queryKey: ['DataSetFunction.list', agentId],
        queryFn: () => AgentDataSet.list({agentId}),
    });

    const onDelete = useMutation({
        mutationFn: AgentDataSet.remove,
        onSuccess: () => {
            message.success('成功!');
            rowSelection.clear();
            getData.refetch();
        },
    });

    const columns: ColumnsType<AgentDataSet.List> = [
        {
            title: '数据集 名称',
            dataIndex: 'name',
        },
        {
            title: '数据集 Code',
            dataIndex: 'code',
        },
        {
            title: (
                <Space>
                    优先级
                    <Tooltip title="数字越小优先级越高">
                        <QuestionCircleOutlined />
                    </Tooltip>
                </Space>
            ),
            dataIndex: 'priority',
        },
        {
            title: '启用状态',
            dataIndex: 'enabled',
            width: 100,
            render: (text: boolean) => <StatusTag status={text} />,
        },
        {
            title: '操作',
            fixed: 'right',
            dataIndex: 'id',
            align: 'center',
            render: (value, record) => (
                <LinkButton
                    onClick={() => {
                        dataSetEditModal.open({
                            agentId,
                            data: record,
                            onOk: () => {
                                dataSetEditModal.close();
                                getData.refetch();
                            },
                        });
                    }}>
                    编辑
                </LinkButton>
            ),
        },
    ];

    function Extra() {
        return (
            <Space>
                <DeleteConfirm
                    onSuccess={() => {
                        onDelete.mutate({
                            agentId,
                            ids: rowSelection.selected,
                        });
                    }}>
                    <Button
                        disabled={rowSelection.isEmpty}
                        danger
                        ghost
                        size="small">
                        批量删除
                    </Button>
                </DeleteConfirm>
                <Button
                    type="primary"
                    size="small"
                    onClick={() => {
                        dataSetSelectDrawer.open({
                            agentId,
                            onOk: () => {
                                dataSetSelectDrawer.close();
                                getData.refetch();
                            },
                        });
                    }}>
                    新增
                </Button>
            </Space>
        );
    }

    return (
        <div className={Style.root}>
            <div className={Style.pd}>
                <Title extra={<Extra />}>数据集</Title>
            </div>
            <Table
                rowKey="id"
                bordered
                size="small"
                columns={columns}
                dataSource={getData.data}
                loading={getData.isLoading || onDelete.isLoading}
                scroll={{x: 'max-content'}}
                pagination={false}
                rowSelection={rowSelection}
            />

            {dataSetSelectDrawer.render()}
            {dataSetEditModal.render()}
        </div>
    );
};

export default Index;
